import { useAtomValue, useSetAtom } from 'jotai'
import { useNavigate, useSearchParams } from 'react-router-dom'
import * as ResetPasswordStore from '../stores/ResetPasswordStore'
import { pageLinks } from '../../pageLinks'

// ============================================================================
// INLINE UI COMPONENTS - self-contained, no external dependencies
// ============================================================================

function Card({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={className} {...props}>
      {children}
    </div>
  )
}

function CardHeader({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={className} {...props}>
      {children}
    </div>
  )
}

function CardTitle({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 className={className} {...props}>
      {children}
    </h3>
  )
}

function CardDescription({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={`text-sm text-neutral-500 ${className}`} {...props}>
      {children}
    </p>
  )
}

function CardContent({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={className} {...props}>
      {children}
    </div>
  )
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'outline'
}

function Button({
  className = '',
  variant = 'default',
  children,
  ...props
}: ButtonProps) {
  const variantStyles =
    variant === 'default'
      ? 'bg-neutral-900 text-white'
      : 'bg-neutral-100 text-neutral-900'

  return (
    <button
      className={`inline-flex items-center justify-center text-sm h-10 px-4 py-2 disabled:opacity-50 disabled:pointer-events-none ${variantStyles} ${className}`}
      {...props}
    >
      {children}
    </button>
  )
}

function Input({
  className = '',
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={`flex h-10 w-full border-b border-neutral-200 bg-transparent px-0 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 disabled:opacity-50 ${className}`}
      {...props}
    />
  )
}

function Label({
  className = '',
  children,
  ...props
}: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label
      className={`text-sm text-neutral-500 ${className}`}
      {...props}
    >
      {children}
    </label>
  )
}

interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'destructive'
}

function Alert({
  className = '',
  variant = 'default',
  children,
  ...props
}: AlertProps) {
  const variantStyles =
    variant === 'destructive'
      ? 'text-red-600'
      : 'text-neutral-900'

  return (
    <div
      role="alert"
      className={`text-sm ${variantStyles} ${className}`}
      {...props}
    >
      {children}
    </div>
  )
}

function AlertDescription({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={`text-sm ${className}`} {...props}>
      {children}
    </p>
  )
}

// ============================================================================
// FIELD COMPONENTS - directly connected to Jotai store
// ============================================================================

function NewPasswordField() {
  const { newPassword } = useAtomValue(ResetPasswordStore.resetPasswordFormAtom)
  const errors = useAtomValue(ResetPasswordStore.resetPasswordFormErrorsAtom)
  const updateForm = useSetAtom(ResetPasswordStore.updateResetPasswordFormAtom)

  return (
    <div className="space-y-1">
      <Label htmlFor="new-password">New Password</Label>
      <Input
        id="new-password"
        type="password"
        autoComplete="new-password"
        required
        minLength={8}
        value={newPassword}
        onChange={(e) => updateForm({ newPassword: e.target.value })}
        placeholder="Min 8 characters"
        aria-invalid={!!errors?.newPassword}
      />
      {errors?.newPassword && (
        <p className="text-xs text-red-600 mt-1">{errors.newPassword}</p>
      )}
    </div>
  )
}

function ConfirmPasswordField() {
  const { confirmPassword } = useAtomValue(
    ResetPasswordStore.resetPasswordFormAtom,
  )
  const errors = useAtomValue(ResetPasswordStore.resetPasswordFormErrorsAtom)
  const updateForm = useSetAtom(ResetPasswordStore.updateResetPasswordFormAtom)

  return (
    <div className="space-y-1">
      <Label htmlFor="confirm-password">Confirm Password</Label>
      <Input
        id="confirm-password"
        type="password"
        autoComplete="new-password"
        required
        minLength={8}
        value={confirmPassword}
        onChange={(e) => updateForm({ confirmPassword: e.target.value })}
        placeholder="Repeat your password"
        aria-invalid={!!errors?.confirmPassword}
      />
      {errors?.confirmPassword && (
        <p className="text-xs text-red-600 mt-1">{errors.confirmPassword}</p>
      )}
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ResetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const { isSubmitting } = useAtomValue(
    ResetPasswordStore.resetPasswordFormAtom,
  )
  const errors = useAtomValue(ResetPasswordStore.resetPasswordFormErrorsAtom)
  const submitResetPassword = useSetAtom(
    ResetPasswordStore.submitResetPasswordAtom,
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (token) {
      await submitResetPassword(token)
      navigate(pageLinks.global_shortcut_login())
    }
  }

  const handleBackToLogin = () => {
    navigate(pageLinks.global_shortcut_login())
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-sm">
          <Card>
            <CardHeader className="text-center pb-6 space-y-1">
              <CardTitle className="text-xl text-neutral-900">
                Link Expired
              </CardTitle>
              <CardDescription>
                This password reset link is invalid or has expired for security
                reasons.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="outline"
                onClick={handleBackToLogin}
                className="w-full"
              >
                Return to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-sm">
        <Card>
          <CardHeader className="text-center pb-6 space-y-1">
            <CardTitle className="text-xl text-neutral-900">
              New Password
            </CardTitle>
            <CardDescription>
              Secure your account with a unique password.
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form className="space-y-6" onSubmit={handleSubmit}>
              {errors?.form && (
                <Alert variant="destructive">
                  <AlertDescription>{errors.form}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-4">
                <NewPasswordField />
                <ConfirmPasswordField />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full"
              >
                {isSubmitting ? 'Updating...' : 'Reset Password'}
              </Button>
            </form>
          </CardContent>

          <div className="pt-4 flex justify-center">
            <button
              type="button"
              onClick={handleBackToLogin}
              className="text-sm text-neutral-400 hover:text-neutral-900"
            >
              Back to Login
            </button>
          </div>
        </Card>
      </div>
    </div>
  )
}
